import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/request'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DEV_LOGIN_ENABLED } from '@/lib/apiBase'
import { useAuth } from '@/providers/AuthProvider'
import title21Logo from '@/title21-logo.png'

const QUICK_PICKS = ['dana.mercer@title21.com', 'admin@title21.com', 'kwame.osei@mayo.example']

function messageOf(err: unknown, fallback: string): string {
  // TypeError: fetch itself failed. 502–504: the dev proxy or load balancer has no API behind it.
  if (err instanceof TypeError || (err instanceof ApiError && err.status >= 502 && err.status <= 504)) {
    return `${fallback}: the API is unreachable.`
  }
  if (err instanceof Error && err.message) return `${fallback}: ${err.message}`
  return fallback
}

export default function LoginPage() {
  const { t } = useTranslation()
  const { isAuthenticated, devLogin, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/queue'

  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  if (isAuthenticated) return <Navigate to={from} replace />

  async function run(action: () => Promise<void>) {
    setError(null)
    setPending(true)
    try {
      await action()
      navigate(from, { replace: true })
    } catch (err) {
      setError(messageOf(err, t('login.failed')))
    } finally {
      setPending(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (DEV_LOGIN_ENABLED) void run(() => devLogin(email.trim()))
    else void run(() => login(username.trim(), password))
  }

  return (
    <div className="flex h-full items-center justify-center bg-surface-subtle px-4">
      <div className="w-full max-w-sm rounded-lg border border-line bg-background p-8">
        <div className="mb-6 flex flex-col gap-3">
          <img src={title21Logo} alt={t('brand.name')} className="h-auto w-full max-w-[240px]" />
          <span className="text-label text-ink-muted">{t('brand.product')}</span>
        </div>
        <h1 className="text-[20px] font-semibold leading-6 tracking-[-0.02em] text-ink">{t('login.title')}</h1>
        <p className="mt-1 text-meta text-ink-muted">{DEV_LOGIN_ENABLED ? t('login.devSubtitle') : t('login.subtitle')}</p>

        <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit}>
          {DEV_LOGIN_ENABLED ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="username">{t('login.username')}</Label>
                <Input
                  id="username"
                  type="email"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="password">{t('login.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </>
          )}

          {error ? (
            <p role="alert" className="rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={pending}>
            {pending ? t('login.submitting') : t('login.submit')}
          </Button>
        </form>

        {DEV_LOGIN_ENABLED ? (
          <div className="mt-6 flex flex-col gap-2">
            <p className="text-eyebrow font-medium uppercase text-ink-muted">{t('login.quickPick')}</p>
            {QUICK_PICKS.map((pick) => (
              <Button
                key={pick}
                type="button"
                variant="outline"
                disabled={pending}
                className="justify-start font-mono text-meta"
                onClick={() => {
                  setEmail(pick)
                  void run(() => devLogin(pick))
                }}
              >
                {pick}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
